import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRightOutlined,
  BulbOutlined,
  CalendarOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  PictureOutlined,
  ReloadOutlined,
  RocketOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { Alert, Button, Drawer, Modal, Tabs, Tag } from 'antd';
import type { CreditBalance, TranslationProgressMap } from './types';
import MarketingCharacterModule from './marketing/MarketingCharacterModule';
import MarketingAssetsWorkspace from './marketing/MarketingAssetsWorkspace';
import MarketingCampaignsModule from './marketing/MarketingCampaignsModule';
import MarketingInspirationModule from './marketing/MarketingInspirationModule';
import MarketingSocialComposer from './marketing/MarketingSocialComposer';
import MarketingSourceKit from './marketing/MarketingSourceKit';
import MarketingVideoModule from './marketing/MarketingVideoModule';
import DeepDiveOffer from './marketing/DeepDiveOffer';
import { DEEP_DIVE_PRICE } from './marketing/deepDiveOfferContent';
import { listM2MHandoffs, m2mErrorMessage } from './marketing/m2mApi';
import { useM2MIdentity } from './marketing/useM2MIdentity';
import M2MReportsModule from './marketing/M2MReportsModule';
import {
  getM2MBlockingHandoff,
  getVerifiedCompletedTranslationPairs,
  isM2MSupportedLanguage,
  M2M_SUPPORTED_LANGUAGES,
} from './marketing/m2mLanguages';
import type {
  M2MHandoffRecord,
  MarketingActionSignal,
  MarketingModuleKey,
  MarketingVideoSimpleGoal,
} from './marketing/types';
import type { MarketingSocialFormat } from './marketing/marketingWorkspaceState';
import {
  createMarketingId,
  loadMarketingWorkspaceState,
  marketingWorkspaceStorageKey,
  saveMarketingWorkspaceState,
  socialDraftKey,
} from './marketing/marketingWorkspaceState';
import { createEmptySocialDraft } from './marketing/socialContent';
import { canonicalizeLanguageKey } from './utils/languageIdentity';
import { getTargetLanguageFromPair, isOriginalLanguageValue } from './utils/languagePairs';
import './MarketingTab.css';

type MarketingView = 'create' | 'book-kit' | 'assets' | 'campaigns' | 'reports';
type MarketingScope = 'edition' | 'book';
type MarketingCreateTool =
  | 'chooser'
  | 'video'
  | 'character'
  | 'story_slides'
  | 'carousel'
  | 'quote_card';
type MarketingAssetFilter = 'all' | 'video' | 'character' | 'social';

interface MarketingTabProps {
  apiBase: string;
  bookId: string;
  userId: string;
  bookTitle?: string;
  bookLanguage?: string;
  selectedLanguage?: string;
  completedTranslationPairs?: string[];
  translationProgress?: TranslationProgressMap;
  m2mGenreDescription?: string;
  m2mNotes?: string;
  actionSignal?: MarketingActionSignal | null;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  onActiveModuleChange?: (module: MarketingModuleKey) => void;
  onGenerateBlurb?: () => void;
  onSuggestKeywords?: () => void;
}

interface InitialMarketingState {
  view: MarketingView;
  module: MarketingModuleKey;
  createTool: MarketingCreateTool;
  assetFilter: MarketingAssetFilter;
  manageBookKit: boolean;
  openM2MModal: boolean;
}

const marketingStudiosFlag = process.env.REACT_APP_ENABLE_MARKETING_STUDIOS;
// Temporarily keep the self-service studios hidden while retaining the M2M
// Deep Dive report flow. An explicit true restores the studios and their tabs.
export const isMarketingStudiosEnabled = (flag?: string): boolean => flag === 'true';
const MARKETING_STUDIOS_ENABLED = isMarketingStudiosEnabled(marketingStudiosFlag);

const resolveInitialState = (marketingStudiosEnabled: boolean): InitialMarketingState => {
  if (typeof window === 'undefined') {
    return {
      view: marketingStudiosEnabled ? 'create' : 'reports',
      module: marketingStudiosEnabled ? 'video' : 'm2m_reports',
      createTool: 'chooser',
      assetFilter: 'all',
      manageBookKit: false,
      openM2MModal: false,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const panel = (params.get('marketingPanel') || '').trim().toLowerCase();
  const requestedView = (params.get('marketingView') || '').trim().toLowerCase();
  const legacyViewMap: Record<string, MarketingView> = {
    overview: 'create',
    'source-kit': 'book-kit',
    library: 'assets',
  };
  const mappedView = legacyViewMap[requestedView] || requestedView;
  const requestedMarketingView: MarketingView = ['create', 'book-kit', 'assets', 'campaigns', 'reports'].includes(mappedView)
    ? mappedView as MarketingView
    : marketingStudiosEnabled ? 'create' : 'reports';
  const view = !marketingStudiosEnabled && requestedMarketingView !== 'reports'
    ? 'reports'
    : requestedMarketingView;
  if (panel === 'm2m' || panel === 'm2m_reports') {
    return {
      view: 'reports',
      module: 'm2m_reports',
      createTool: 'chooser',
      assetFilter: 'all',
      manageBookKit: false,
      openM2MModal: true,
    };
  }
  if (marketingStudiosEnabled && panel === 'character-assets') {
    return {
      view: 'assets',
      module: 'character_assets',
      createTool: 'chooser',
      assetFilter: 'character',
      manageBookKit: false,
      openM2MModal: false,
    };
  }
  if (marketingStudiosEnabled && panel === 'characters') {
    return {
      view: 'create',
      module: 'characters',
      createTool: 'character',
      assetFilter: 'all',
      manageBookKit: false,
      openM2MModal: false,
    };
  }
  if (marketingStudiosEnabled && panel === 'inspiration') {
    return {
      view: 'book-kit',
      module: 'inspiration',
      createTool: 'chooser',
      assetFilter: 'all',
      manageBookKit: true,
      openM2MModal: false,
    };
  }
  if (marketingStudiosEnabled && panel === 'assets') {
    return {
      view: 'assets',
      module: 'assets',
      createTool: 'chooser',
      assetFilter: 'video',
      manageBookKit: false,
      openM2MModal: false,
    };
  }
  if (marketingStudiosEnabled && panel === 'video') {
    return {
      view: 'create',
      module: 'video',
      createTool: 'video',
      assetFilter: 'all',
      manageBookKit: false,
      openM2MModal: false,
    };
  }
  return {
    view,
    module: marketingStudiosEnabled ? (view === 'campaigns' ? 'campaigns' : view === 'book-kit' ? 'source_kit' : view === 'assets' ? 'assets' : 'video') : 'm2m_reports',
    createTool: 'chooser',
    assetFilter: 'all',
    manageBookKit: false,
    openM2MModal: false,
  };
};

const renderMarketingModalTitle = (title: string) => (
  <div className="translation-modal-titlebar">
    <span className="translation-modal-titlebar__title">{title}</span>
  </div>
);

interface MarketingWorkspaceRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  scope: MarketingScope;
  actionLabel?: string;
  actionAriaLabel?: string;
  onAction?: () => void;
  planned?: boolean;
  disabled?: boolean;
  statusLabel?: string;
}

const MarketingWorkspaceRow: React.FC<MarketingWorkspaceRowProps> = ({
  icon,
  title,
  description,
  scope,
  actionLabel,
  actionAriaLabel,
  onAction,
  planned = false,
  disabled = false,
  statusLabel,
}) => (
  <div className="marketing-workspace-row">
    <span className="marketing-workspace-row__icon" aria-hidden="true">{icon}</span>
    <div className="marketing-workspace-row__content">
      <div className="marketing-workspace-row__title">
        <strong>{title}</strong>
        {scope === 'book' ? <Tag>Book-wide</Tag> : null}
        {planned ? <Tag>Planned</Tag> : null}
        {statusLabel ? <Tag>{statusLabel}</Tag> : null}
      </div>
      <span>{description}</span>
    </div>
    {actionLabel ? (
      <Button
        aria-label={actionAriaLabel || actionLabel}
        icon={planned ? undefined : <ArrowRightOutlined />}
        disabled={planned || disabled}
        onClick={() => onAction?.()}
      >
        {actionLabel}
      </Button>
    ) : null}
  </div>
);

const MarketingWorkspaceSection: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="marketing-workspace-section">
    <div className="marketing-workspace-section__header">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
    <div className="marketing-workspace-section__list">{children}</div>
  </section>
);

const M2MUnavailableNotice: React.FC<{ language: string }> = ({ language }) => (
  <section className="marketing-m2m-unavailable" aria-label="Deep Dive report unavailable">
    <Alert
      type="warning"
      showIcon
      message={`Deep Dive reports are not available for ${language}`}
      description={(
        <div>
          <p>
            Novel Publicity currently supports Deep Dive reports for the languages listed below.
            Select a supported language edition to continue.
          </p>
          <p className="marketing-m2m-unavailable__languages">
            <strong>Available languages:</strong> {M2M_SUPPORTED_LANGUAGES.join(', ')}
          </p>
        </div>
      )}
    />
  </section>
);

const MarketingWorkspace: React.FC<MarketingTabProps> = ({
  apiBase,
  bookId,
  userId,
  bookTitle,
  bookLanguage,
  selectedLanguage,
  completedTranslationPairs,
  translationProgress,
  m2mGenreDescription,
  m2mNotes,
  actionSignal,
  creditBalance,
  fetchCreditBalance,
  onActiveModuleChange,
  onGenerateBlurb,
  onSuggestKeywords,
}) => {
  const initialState = useMemo(() => resolveInitialState(MARKETING_STUDIOS_ENABLED), []);
  const [activeView, setActiveView] = useState<MarketingView>(initialState.view);
  const [activeModule, setActiveModule] = useState<MarketingModuleKey>(initialState.module);
  const [createTool, setCreateTool] = useState<MarketingCreateTool>(initialState.createTool);
  const [openedCreateTools, setOpenedCreateTools] = useState<MarketingCreateTool[]>(
    initialState.createTool === 'chooser' ? [] : [initialState.createTool]
  );
  const [assetFilter, setAssetFilter] = useState<MarketingAssetFilter>(initialState.assetFilter);
  const [manageBookKit, setManageBookKit] = useState(initialState.manageBookKit);
  const [m2mModalOpen, setM2MModalOpen] = useState(initialState.openM2MModal);
  const [m2mInfoOpen, setM2MInfoOpen] = useState(false);
  const [videoInitialGoal, setVideoInitialGoal] = useState<MarketingVideoSimpleGoal | undefined>();
  const [videoGoalRequestId, setVideoGoalRequestId] = useState(0);
  const [m2mRefreshSignal, setM2MRefreshSignal] = useState(0);
  const [sourceKitRefreshSignal, setSourceKitRefreshSignal] = useState(0);
  const [sourceDataRevision, setSourceDataRevision] = useState(0);
  const [workspaceRefreshSignal, setWorkspaceRefreshSignal] = useState(0);
  const m2mAvailabilityRequest = useRef<AbortController>();
  const [m2mAvailabilityError, setM2MAvailabilityError] = useState<string>();
  const [m2mAvailabilityLoaded, setM2MAvailabilityLoaded] = useState(false);
  const [m2mHandoffs, setM2MHandoffs] = useState<M2MHandoffRecord[]>([]);
  const [workspaceState, setWorkspaceState] = useState(() => loadMarketingWorkspaceState(userId, bookId));
  const workspaceStorageScope = marketingWorkspaceStorageKey(userId, bookId);
  const loadedWorkspaceScopeRef = useRef(workspaceStorageScope);
  const skipWorkspaceSaveScopeRef = useRef<string | null>(null);
  const lastProcessedActionTsRef = useRef<number | null>(null);
  const lastCreditFetchUserIdRef = useRef<string | null>(null);
  const sourceLanguage = (bookLanguage || 'Original').trim() || 'Original';
  const isSourceLanguage = !selectedLanguage || isOriginalLanguageValue(selectedLanguage);
  const editionLanguage = isSourceLanguage
    ? sourceLanguage
    : getTargetLanguageFromPair(selectedLanguage) || selectedLanguage || sourceLanguage;
  const verifiedCompletedTranslationPairs = useMemo(
    () => getVerifiedCompletedTranslationPairs(completedTranslationPairs, translationProgress),
    [completedTranslationPairs, translationProgress]
  );
  const selectedEditionTranslationComplete = useMemo(() => {
    if (isSourceLanguage) return true;
    const selectedKey = canonicalizeLanguageKey(editionLanguage);
    return verifiedCompletedTranslationPairs.some(
      pair => canonicalizeLanguageKey(getTargetLanguageFromPair(pair)) === selectedKey
    );
  }, [editionLanguage, isSourceLanguage, verifiedCompletedTranslationPairs]);
  const selectedEditionSupportsM2M = !isSourceLanguage && isM2MSupportedLanguage(editionLanguage);
  const selectedEditionCanUseM2M = selectedEditionSupportsM2M && selectedEditionTranslationComplete;
  const selectedEditionBlockingHandoff = useMemo(
    () => getM2MBlockingHandoff(m2mHandoffs, editionLanguage),
    [editionLanguage, m2mHandoffs]
  );
  const selectedEditionHasM2MReport = Boolean(selectedEditionBlockingHandoff);
  const selectedEditionM2MReportCompleted =
    String(selectedEditionBlockingHandoff?.status || '').toLowerCase() === 'completed';
  const canCreateM2MReport = m2mAvailabilityLoaded && selectedEditionCanUseM2M && !selectedEditionHasM2MReport;
  const m2mAvailabilityLabel = !selectedEditionSupportsM2M
    ? 'Unavailable'
    : !selectedEditionTranslationComplete
      ? 'Translation incomplete'
      : selectedEditionHasM2MReport
        ? selectedEditionM2MReportCompleted ? 'Completed' : 'Report in progress'
        : undefined;
  const m2mAvailabilityDescription = !selectedEditionSupportsM2M
    ? `Deep Dive reports are not available for ${editionLanguage}.`
    : !selectedEditionTranslationComplete
      ? `Complete the ${editionLanguage} translation before ordering a Deep Dive report.`
      : selectedEditionHasM2MReport
        ? selectedEditionM2MReportCompleted
          ? `A Deep Dive report has already been completed for ${editionLanguage}.`
          : `A Deep Dive report is already in progress for ${editionLanguage}.`
        : `The ${editionLanguage} translation is ready for a Deep Dive report.`;

  useEffect(() => {
    if (loadedWorkspaceScopeRef.current === workspaceStorageScope) return;
    loadedWorkspaceScopeRef.current = workspaceStorageScope;
    skipWorkspaceSaveScopeRef.current = workspaceStorageScope;
    setWorkspaceState(loadMarketingWorkspaceState(userId, bookId));
  }, [bookId, userId, workspaceStorageScope]);

  useEffect(() => {
    if (loadedWorkspaceScopeRef.current !== workspaceStorageScope) return;
    if (skipWorkspaceSaveScopeRef.current === workspaceStorageScope) {
      skipWorkspaceSaveScopeRef.current = null;
      return;
    }
    saveMarketingWorkspaceState(userId, bookId, workspaceState);
  }, [bookId, userId, workspaceState, workspaceStorageScope]);

  const refreshM2MHandoffAvailability = useCallback(async () => {
    if (!bookId || !userId) return setM2MHandoffs([]);
    m2mAvailabilityRequest.current?.abort();
    const controller = new AbortController();
    m2mAvailabilityRequest.current = controller;
    setM2MAvailabilityLoaded(false);
    setM2MAvailabilityError(undefined);
    try {
      const data = await listM2MHandoffs({ apiBase, bookId, userId }, controller.signal);
      if (!controller.signal.aborted) {
        setM2MHandoffs(Array.isArray(data?.items) ? data.items : []);
        setM2MAvailabilityLoaded(!data.hasMore);
        if (data.hasMore) setM2MAvailabilityError('Report history is incomplete. Refresh before placing another order.');
      }
    } catch (error) {
      if (!controller.signal.aborted) setM2MAvailabilityError(m2mErrorMessage(error, 'Unable to load Deep Dive report availability. Please refresh.'));
    }
  }, [apiBase, bookId, userId]);

  useEffect(() => {
    void refreshM2MHandoffAvailability();
    return () => m2mAvailabilityRequest.current?.abort();
  }, [refreshM2MHandoffAvailability]);
  const refreshM2MReports = useCallback(() => {
    setM2MRefreshSignal(value => value + 1);
    void refreshM2MHandoffAvailability();
  }, [refreshM2MHandoffAvailability]);

  useEffect(() => {
    if (!userId || !fetchCreditBalance) return;
    if (creditBalance && typeof (creditBalance.available_balance ?? creditBalance.credit_balance) === 'number') {
      lastCreditFetchUserIdRef.current = userId;
      return;
    }
    if (lastCreditFetchUserIdRef.current === userId) return;
    lastCreditFetchUserIdRef.current = userId;
    Promise.resolve(fetchCreditBalance(userId)).catch(() => { lastCreditFetchUserIdRef.current = null; });
  }, [creditBalance, fetchCreditBalance, userId]);

  useEffect(() => { onActiveModuleChange?.(activeModule); }, [activeModule, onActiveModuleChange]);

  const markToolOpened = useCallback((tool: MarketingCreateTool) => {
    setOpenedCreateTools(current => current.includes(tool) ? current : [...current, tool]);
  }, []);
  const markSourcesChanged = useCallback(() => {
    setSourceDataRevision(revision => revision + 1);
  }, []);
  const openVideoStudio = useCallback((goal?: MarketingVideoSimpleGoal) => {
    setVideoInitialGoal(goal);
    setVideoGoalRequestId(value => value + 1);
    setActiveView('create');
    setCreateTool('video');
    markToolOpened('video');
    setActiveModule('video');
  }, [markToolOpened]);
  const openCharacterStudio = useCallback(() => {
    setActiveView('create');
    setCreateTool('character');
    markToolOpened('character');
    setActiveModule('characters');
  }, [markToolOpened]);
  const openSocialComposer = useCallback((format: MarketingSocialFormat) => {
    setActiveView('create');
    setCreateTool(format);
    markToolOpened(format);
    setActiveModule('social');
  }, [markToolOpened]);
  const openSourceKit = useCallback(() => {
    setActiveView('book-kit');
    setManageBookKit(false);
    setActiveModule('source_kit');
  }, []);
  const openInspirationLibrary = useCallback(() => {
    setActiveView('book-kit');
    setManageBookKit(true);
    setActiveModule('inspiration');
  }, []);
  const openMarketingAssets = useCallback(() => {
    setAssetFilter('video');
    setActiveView('assets');
    setActiveModule('assets');
  }, []);
  const openCharacterAssets = useCallback(() => {
    setAssetFilter('character');
    setActiveView('assets');
    setActiveModule('character_assets');
  }, []);
  const openM2MReports = useCallback(() => {
    if (!isSourceLanguage && !canCreateM2MReport) return;
    if (!isSourceLanguage) setActiveView('reports');
    setActiveModule('m2m_reports');
    setM2MModalOpen(true);
  }, [canCreateM2MReport, isSourceLanguage]);

  const activeSocialFormat = ['story_slides', 'carousel', 'quote_card'].includes(createTool)
    ? createTool as MarketingSocialFormat
    : null;
  const activeDraftKey = activeSocialFormat ? socialDraftKey(editionLanguage, activeSocialFormat) : '';
  const activeSocialDraft = activeSocialFormat
    ? workspaceState.socialDrafts[activeDraftKey] || createEmptySocialDraft(activeSocialFormat, editionLanguage, bookTitle || 'Book')
    : null;

  useEffect(() => {
    if (!actionSignal?.ts || lastProcessedActionTsRef.current === actionSignal.ts) return;
    lastProcessedActionTsRef.current = actionSignal.ts;
    if (actionSignal.action === 'generate' || actionSignal.action === 'copy') {
      if (activeModule === 'characters' || activeModule === 'character_assets') openCharacterStudio();
      else if (activeModule === 'social' && activeSocialFormat) openSocialComposer(activeSocialFormat);
      else openVideoStudio();
      return;
    }
    if (actionSignal.action === 'refresh') {
      setWorkspaceRefreshSignal(value => value + 1);
      if (activeModule === 'm2m_reports') refreshM2MReports();
      else if (activeModule === 'source_kit' || activeModule === 'inspiration') setSourceKitRefreshSignal(value => value + 1);
    }
  }, [actionSignal, activeModule, activeSocialFormat, openCharacterStudio, openSocialComposer, openVideoStudio, refreshM2MReports]);

  const selectView = (view: MarketingView) => {
    setActiveView(view);
    if (view === 'book-kit') setActiveModule(manageBookKit ? 'inspiration' : 'source_kit');
    if (view === 'assets') setActiveModule('assets');
    if (view === 'campaigns') setActiveModule('campaigns');
    if (view === 'reports') setActiveModule('m2m_reports');
    if (view === 'create') {
      if (createTool === 'character') setActiveModule('characters');
      else if (activeSocialFormat) setActiveModule('social');
      else setActiveModule('video');
    }
  };

  const creatorPath = createTool === 'character'
    ? 'Character portrait'
    : activeSocialFormat
      ? activeSocialFormat === 'story_slides' ? 'TikTok story slides' : activeSocialFormat === 'quote_card' ? 'Quote card' : 'Carousel'
      : videoInitialGoal === 'animated_cover' ? 'Animated cover' : videoInitialGoal === 'launch_teaser' ? 'Launch teaser' : 'Scene video';

  return (
    <>
      <section className="marketing-workspace" aria-label="Marketing workspace">
        {m2mAvailabilityError && <Alert type="error" showIcon message={m2mAvailabilityError} action={<Button onClick={() => void refreshM2MHandoffAvailability()}>Refresh</Button>} />}
        {MARKETING_STUDIOS_ENABLED ? (
          <Tabs
            className="marketing-workspace__tabs"
            activeKey={activeView}
            onChange={key => selectView(key as MarketingView)}
            tabBarExtraContent={(
              <Button size="small" icon={<FileTextOutlined />} onClick={() => setM2MInfoOpen(true)}>
                Deep Dive reports
              </Button>
            )}
            items={[
              { key: 'create', label: 'Create' },
              { key: 'book-kit', label: 'Book Kit' },
              { key: 'assets', label: 'Assets' },
              { key: 'campaigns', label: 'Campaigns' },
              ...(!isSourceLanguage || activeView === 'reports' ? [{ key: 'reports', label: 'Reports' }] : []),
            ]}
          />
        ) : null}

        {MARKETING_STUDIOS_ENABLED ? (
          <div
            hidden={activeView !== 'create'}
            style={activeView !== 'create' ? { display: 'none' } : undefined}
            className="marketing-workspace-view"
          >
            <div hidden={createTool !== 'chooser'} style={createTool !== 'chooser' ? { display: 'none' } : undefined}>
              <section className="marketing-workspace-page-header">
                <div>
                  <span className="marketing-workspace__kicker">Create for {editionLanguage}</span>
                  <h3>What do you want to make?</h3>
                  <p>Start with an outcome. Recommended sources and technical settings are applied for you.</p>
                </div>
                <Button icon={<BulbOutlined />} aria-label="Review Book Kit" onClick={openSourceKit}>Review Book Kit</Button>
              </section>
              <div className="marketing-outcome-grid">
                <MarketingWorkspaceSection title="Motion" description="Platform-ready video built with guided defaults.">
                  <MarketingWorkspaceRow icon={<PictureOutlined />} title="Animated cover" description="Turn the current cover into a short motion clip." scope="edition" actionLabel="Start" actionAriaLabel="Open Video Studio" onAction={() => openVideoStudio('animated_cover')} />
                  <MarketingWorkspaceRow icon={<VideoCameraOutlined />} title="Scene video" description="Tease an approved scene with its characters, setting, and beats." scope="edition" actionLabel="Start" onAction={() => openVideoStudio('world_mood_teaser')} />
                  <MarketingWorkspaceRow icon={<RocketOutlined />} title="Launch teaser" description="Lead with the hook, genre, atmosphere, and cover." scope="edition" actionLabel="Start" onAction={() => openVideoStudio('launch_teaser')} />
                </MarketingWorkspaceSection>
                <MarketingWorkspaceSection title="Characters and social" description="Create still references or editable social copy packs.">
                  <MarketingWorkspaceRow icon={<TeamOutlined />} title="Character portrait" description="Build a consistent visual reference from the manuscript description." scope="book" actionLabel="Start" actionAriaLabel="Open Character Studio" onAction={openCharacterStudio} />
                  <MarketingWorkspaceRow icon={<CalendarOutlined />} title="TikTok story slides" description="Split an approved passage into an editable multi-slide excerpt." scope="edition" actionLabel="Start" actionAriaLabel="Create TikTok story slides" onAction={() => openSocialComposer('story_slides')} />
                  <MarketingWorkspaceRow icon={<PictureOutlined />} title="Carousel" description="Build an editable multi-card hook for social platforms." scope="edition" actionLabel="Start" actionAriaLabel="Create social carousel" onAction={() => openSocialComposer('carousel')} />
                  <MarketingWorkspaceRow icon={<FileTextOutlined />} title="Quote card" description="Turn an approved exact quote into a reusable card brief." scope="edition" actionLabel="Start" actionAriaLabel="Create quote card" onAction={() => openSocialComposer('quote_card')} />
                </MarketingWorkspaceSection>
              </div>
              {(onGenerateBlurb || onSuggestKeywords) && isSourceLanguage ? (
                <div className="marketing-create-foundation-actions">
                  <span>Need sales copy too?</span>
                  {onGenerateBlurb ? <Button size="small" onClick={onGenerateBlurb}>Generate book description</Button> : null}
                  {onSuggestKeywords ? <Button size="small" onClick={onSuggestKeywords}>Suggest keywords</Button> : null}
                </div>
              ) : null}
              <section className="marketing-deep-dive-feature" aria-label="Featured Deep Dive reports">
                <div className="marketing-deep-dive-feature__icon" aria-hidden="true">
                  <FileTextOutlined />
                </div>
                <div className="marketing-deep-dive-feature__content">
                  <span className="marketing-workspace__kicker">Professional marketing strategy</span>
                  <h3>Take your translated edition further with a Deep Dive report</h3>
                  <p>
                    Novel Publicity analyzes the translated manuscript and delivers market positioning,
                    localized sales copy, Amazon intelligence, ad concepts, and excerpt recommendations.
                  </p>
                  <div className="marketing-deep-dive-feature__details">
                    <Tag color="blue">{DEEP_DIVE_PRICE}</Tag>
                    <span>English-readable strategy</span>
                    <span>Localized, market-ready assets</span>
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={() => {
                    if (!isSourceLanguage && selectedEditionCanUseM2M) {
                      if (selectedEditionHasM2MReport) selectView('reports');
                      else openM2MReports();
                      return;
                    }
                    setM2MInfoOpen(true);
                  }}
                >
                  {isSourceLanguage
                    ? 'Explore Deep Dive reports'
                    : selectedEditionHasM2MReport
                      ? 'View Deep Dive reports'
                      : canCreateM2MReport
                        ? 'Start Deep Dive report'
                        : 'See availability'}
                </Button>
              </section>
            </div>

            {createTool !== 'chooser' ? (
              <div className="marketing-creator-toolbar">
                <Button onClick={() => setCreateTool('chooser')}>← All outcomes</Button>
                <div><strong>{creatorPath}</strong><span>{editionLanguage} edition</span></div>
                <Button onClick={openSourceKit}>Open Book Kit</Button>
              </div>
            ) : null}

            {openedCreateTools.includes('video') ? (
              <div hidden={createTool !== 'video'} style={createTool !== 'video' ? { display: 'none' } : undefined} className="marketing-inline-tool">
                <MarketingVideoModule
                  apiBase={apiBase}
                  bookId={bookId}
                  userId={userId}
                  bookTitle={bookTitle}
                  sourceLanguage={sourceLanguage}
                  selectedLanguage={editionLanguage}
                  actionSignal={activeView === 'create' && createTool === 'video' ? actionSignal : null}
                  creditBalance={creditBalance}
                  fetchCreditBalance={fetchCreditBalance}
                  initialStudioView="create"
                  allowedStudioViews={['create']}
                  initialSimpleGoalId={videoInitialGoal}
                  initialGoalRequestId={videoGoalRequestId}
                  inspirationRefreshSignal={sourceDataRevision}
                  onOpenSourceKit={openSourceKit}
                />
              </div>
            ) : null}
            {openedCreateTools.includes('character') ? (
              <div hidden={createTool !== 'character'} style={createTool !== 'character' ? { display: 'none' } : undefined} className="marketing-inline-tool">
                <MarketingCharacterModule
                  apiBase={apiBase}
                  bookId={bookId}
                  userId={userId}
                  bookTitle={bookTitle}
                  actionSignal={activeView === 'create' && createTool === 'character' ? actionSignal : null}
                  creditBalance={creditBalance}
                  fetchCreditBalance={fetchCreditBalance}
                  initialStudioView="create"
                  allowedStudioViews={['create']}
                  inspirationRefreshSignal={sourceDataRevision}
                />
              </div>
            ) : null}
            {activeSocialFormat && activeSocialDraft ? (
              <div className="marketing-inline-tool">
                <MarketingSocialComposer
                  apiBase={apiBase}
                  bookId={bookId}
                  userId={userId}
                  sourceLanguage={sourceLanguage}
                  editionLanguage={editionLanguage}
                  bookTitle={bookTitle}
                  draft={activeSocialDraft}
                  onDraftChange={draft => setWorkspaceState(current => ({
                    ...current,
                    socialDrafts: { ...current.socialDrafts, [activeDraftKey]: draft },
                  }))}
                  onSaveAsset={draft => {
                    const asset = { ...draft, id: createMarketingId('social'), createdAt: new Date().toISOString() };
                    setWorkspaceState(current => ({ ...current, socialAssets: [asset, ...current.socialAssets] }));
                    return asset;
                  }}
                  onOpenBookKit={openSourceKit}
                  sourceRefreshSignal={sourceDataRevision}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {MARKETING_STUDIOS_ENABLED && activeView === 'book-kit' ? (
          <div className="marketing-workspace-view">
            {manageBookKit ? (
              <>
                <div className="marketing-creator-toolbar">
                  <Button onClick={() => { setManageBookKit(false); setActiveModule('source_kit'); }}>← Quick review</Button>
                  <div><strong>Advanced Book Kit manager</strong><span>Edit sources, filters, extraction runs, and provenance.</span></div>
                </div>
                <MarketingInspirationModule apiBase={apiBase} bookId={bookId} userId={userId} sourceLanguage={sourceLanguage} editionLanguage={editionLanguage} actionSignal={activeView === 'book-kit' ? actionSignal : null} onSourcesChanged={markSourcesChanged} />
              </>
            ) : (
              <MarketingSourceKit
                apiBase={apiBase}
                bookId={bookId}
                userId={userId}
                sourceLanguage={sourceLanguage}
                editionLanguage={editionLanguage}
                onManageSources={openInspirationLibrary}
                onCreateCharacter={openCharacterStudio}
                onBrowseCharacterImages={openCharacterAssets}
                onCreateFromSources={() => openVideoStudio('world_mood_teaser')}
                onSourcesChanged={markSourcesChanged}
                refreshSignal={sourceKitRefreshSignal}
              />
            )}
          </div>
        ) : null}

        {MARKETING_STUDIOS_ENABLED && activeView === 'assets' ? (
          <MarketingAssetsWorkspace
            key={`assets-${workspaceRefreshSignal}`}
            apiBase={apiBase}
            bookId={bookId}
            userId={userId}
            editionLanguage={editionLanguage}
            socialAssets={workspaceState.socialAssets}
            onDeleteSocialAsset={assetId => setWorkspaceState(current => ({
              ...current,
              socialAssets: current.socialAssets.filter(asset => asset.id !== assetId),
            }))}
            onCreateAsset={() => { setCreateTool('chooser'); selectView('create'); }}
            initialFilter={assetFilter}
          />
        ) : null}

        {MARKETING_STUDIOS_ENABLED && activeView === 'campaigns' ? (
          <MarketingCampaignsModule
            apiBase={apiBase}
            bookId={bookId}
            userId={userId}
            editionLanguage={editionLanguage}
            campaigns={workspaceState.campaigns}
            socialAssets={workspaceState.socialAssets}
            onCampaignsChange={campaigns => setWorkspaceState(current => ({ ...current, campaigns }))}
          />
        ) : null}

        {(!MARKETING_STUDIOS_ENABLED || activeView === 'reports') ? (
          <div className="marketing-workspace-view marketing-workspace-view--reports">
            {isSourceLanguage ? (
              <DeepDiveOffer actionLabel="Start Deep Dive report" onAction={openM2MReports} />
            ) : !selectedEditionSupportsM2M ? (
              <M2MUnavailableNotice language={editionLanguage} />
            ) : (
              <>
                <div className="marketing-workspace-reports-header">
                  <div>
                    <div className="marketing-workspace-reports-header__title"><h3>Deep Dive reports</h3>{m2mAvailabilityLabel ? <Tag>{m2mAvailabilityLabel}</Tag> : null}</div>
                    <p>{canCreateM2MReport ? 'Review delivered reports and active orders for this book.' : m2mAvailabilityDescription}</p>
                  </div>
                  {selectedEditionCanUseM2M ? (
                    <div className="marketing-workspace-reports-header__actions">
                      <Button icon={<InfoCircleOutlined />} onClick={() => setM2MInfoOpen(true)}>About</Button>
                      <Button icon={<ReloadOutlined />} onClick={refreshM2MReports}>Refresh</Button>
                      <Button type="primary" icon={<PlusOutlined />} aria-label="New M2M report" onClick={openM2MReports} disabled={!canCreateM2MReport}>New Deep Dive report</Button>
                    </div>
                  ) : null}
                </div>
                {selectedEditionCanUseM2M ? (
                  <M2MReportsModule apiBase={apiBase} bookId={bookId} userId={userId} bookTitle={bookTitle} bookLanguage={bookLanguage} selectedLanguage={selectedLanguage} completedTranslationPairs={verifiedCompletedTranslationPairs} actionSignal={actionSignal} displayMode="reports" refreshSignal={m2mRefreshSignal} showHeader={false} existingHandoffs={m2mHandoffs} onHandoffsChange={setM2MHandoffs}
                    emptyState={<DeepDiveOffer statusMessage={m2mAvailabilityDescription} actionLabel="Order Deep Dive report" actionDisabled={!canCreateM2MReport} onAction={openM2MReports} />} />
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </section>

      <Drawer title="About Deep Dive reports" open={m2mInfoOpen} onClose={() => setM2MInfoOpen(false)} width="min(620px, calc(100vw - 20px))" className="marketing-m2m-about-drawer">
        <DeepDiveOffer
          drawer
          statusLabel={isSourceLanguage ? undefined : m2mAvailabilityLabel}
          statusMessage={isSourceLanguage ? undefined : m2mAvailabilityDescription}
          actionLabel={isSourceLanguage ? 'Start Deep Dive report' : canCreateM2MReport ? 'Order Deep Dive report' : undefined}
          onAction={(isSourceLanguage || canCreateM2MReport) ? openM2MReports : undefined}
        />
      </Drawer>

      <Modal title={renderMarketingModalTitle('New Deep Dive report')} open={m2mModalOpen} onCancel={() => setM2MModalOpen(false)} footer={null} centered className="translation-modal-footer-stable m2m-reports-modal" width="min(720px, calc(100vw - 32px))" bodyStyle={{ maxHeight: 'min(78vh, calc(100vh - 180px))', overflowY: 'auto', overflowX: 'hidden', padding: '16px 16px 12px' }} destroyOnClose={false}>
        <M2MReportsModule
          apiBase={apiBase}
          bookId={bookId}
          userId={userId}
          bookTitle={bookTitle}
          bookLanguage={bookLanguage}
          selectedLanguage={selectedLanguage}
          completedTranslationPairs={verifiedCompletedTranslationPairs}
          existingHandoffs={m2mHandoffs}
          initialGenreDescription={m2mGenreDescription}
          initialNotes={m2mNotes}
          actionSignal={actionSignal}
          displayMode="setup"
          onHandoffCreated={handoff => {
            if (handoff?.handoffId) setM2MHandoffs(current => [handoff, ...current.filter(item => item.handoffId !== handoff.handoffId)]);
            setM2MModalOpen(false);
            refreshM2MReports();
          }}
        />
      </Modal>
    </>
  );
};

const MarketingTab: React.FC<MarketingTabProps> = props => {
  const authorized = useM2MIdentity(props.userId);
  return authorized
    ? <MarketingWorkspace key={`${props.userId}:${props.bookId}:${props.apiBase}`} {...props} />
    : <Alert type="info" message="Sign in to access Marketing." />;
};

export default MarketingTab;
